/**
 * QUIPWA — the note model shared by the repositories, the sync and the UI
 *
 * Pure types and functions: no storage, no Drive. See docs/modulos/notas.md.
 *
 * Identity. `file_id` is the note's **stable id**, whatever its name says:
 *
 *   - notes that already existed on Drive keep their Drive file id;
 *   - notes created while a pre-generated Drive id was available
 *     (`files.generateIds`) are born with their final Drive id;
 *   - notes created with no id at hand (never signed in, pool empty) get a
 *     `local-…` id. It never changes: Drive stores it in the file's
 *     `appProperties.quipwaId`, so every device maps the file back to it.
 *
 * Routes (`/notes/:id`), embeds and flashcard links therefore never need
 * rewriting after a sync.
 */
import { resolveNoteByTitle } from './course-promotion'
import { parseFrontmatter as parseRaw, readList, readString, stringifyFrontmatter } from './frontmatter'
import { countUnlinkedMentions } from './unlinked-mentions'

export type NoteTypeName = 'Book' | 'Quick Note' | 'Short Course' | 'Long Course'

/** A note as lists, search and the directory see it (no body). */
export interface NoteMetadata {
  /** Stable note id — see the header. */
  file_id: string
  filename: string
  title: string
  tags: string[]
  domain?: string | undefined
  level?: string | undefined
  status?: string | undefined
  type?: string | undefined
  /** For a module of a Long Course: the course's note id. */
  parent_id?: string | undefined
  /** Drive folder of a Long Course (and of its modules), once it exists on Drive. */
  drive_folder_id?: string | undefined
  /** Seconds since epoch. */
  modified_at: number
  size_bytes?: number | undefined
  /** True while this device has changes Drive does not have yet. */
  pending?: boolean | undefined
}

/** An opened note: its body without the frontmatter, plus the frontmatter. */
export interface NoteContent {
  file_id: string
  filename: string
  content: string
  frontmatter: Record<string, unknown>
}

/** A `[[link]]` from another note to this one. */
export interface Backlink {
  from_note_id: string
  to_note_id: string
  link_text: string
}

export interface UnlinkedMention {
  fileId: string
  title: string
  count: number
}

// ── ids ─────────────────────────────────────────────────────────────────────

export const LOCAL_ID_PREFIX = 'local-'

export function newLocalNoteId(): string {
  return `${LOCAL_ID_PREFIX}${crypto.randomUUID()}`
}

/** Could Drive accept this id as a file id? (Only ids from `generateIds`.) */
export function isDriveId(id: string): boolean {
  return !id.startsWith(LOCAL_ID_PREFIX)
}

// ── frontmatter → metadata ─────────────────────────────────────────────────

export interface NoteFields {
  title: string
  type: string
  status: string
  domain: string
  tags: string[]
  level?: string | undefined
}

/**
 * The listing fields of a note, read from its full Markdown. Lookups ignore
 * case (files arrive from Obsidian and hand editing); a note without a title
 * falls back to its filename.
 */
export function fieldsFromMarkdown(markdown: string, filename: string): NoteFields {
  const { frontmatter } = parseRaw(markdown)
  return {
    title: readString(frontmatter, 'title') ?? filename.replace(/\.md$/i, ''),
    type: readString(frontmatter, 'type') ?? 'Quick Note',
    status: readString(frontmatter, 'status') ?? 'Pending',
    domain: readString(frontmatter, 'domain') ?? '',
    tags: readList(frontmatter, 'tags'),
    level: readString(frontmatter, 'level'),
  }
}

/** Split a full Markdown file into the editor's body and its frontmatter. */
export function splitNote(markdown: string): { content: string; frontmatter: Record<string, unknown> } {
  const { frontmatter, body } = parseRaw(markdown)
  return { content: body, frontmatter }
}

/** Join the editor's body and frontmatter back into the file's Markdown. */
export function joinNote(content: string, frontmatter: Record<string, unknown>): string {
  return stringifyFrontmatter(frontmatter, content)
}

// ── filenames ───────────────────────────────────────────────────────────────

export const COURSE_INDEX_FILENAME = '_index.md'

/** `Álgebra Básica` → `álgebra_básica.md`. Names need not be unique: Drive uses ids. */
export function noteFilename(title: string): string {
  const safe = (title || 'nota')
    .trim()
    .toLowerCase()
    .replace(/[\\/:*?"<>|#%{}^~[\]`']/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 80)
  return `${safe || 'nota'}.md`
}

// ── links ───────────────────────────────────────────────────────────────────

const LINK_RE = /!?\[\[([^\]|\n]+)(?:\|([^\]\n]+))?\]\]/g

/** Every `[[Target]]`, `[[Target|alias]]` and `![[Embed]]` in a text, in order. */
export function extractLinkTargets(markdown: string): Array<{ target: string; text: string }> {
  const out: Array<{ target: string; text: string }> = []
  for (const m of markdown.matchAll(LINK_RE)) {
    const target = m[1]!.trim()
    out.push({ target, text: m[2]?.trim() || target })
  }
  return out
}

/** What the link scans need from each note. */
export interface LinkableNote {
  id: string
  title: string
  type?: string | undefined
  /** Full Markdown (frontmatter included). */
  body: string
}

/**
 * Notes that `[[link]]` (or embed) `targetId`. Each link is resolved the way
 * the editor resolves it (`resolveNoteByTitle`: when titles repeat, the
 * non-course note wins), so a backlink appears exactly where clicking the link
 * would land. Computed from the bodies every device syncs, so backlinks from
 * notes written on another device show up too.
 */
export function computeBacklinks(notes: readonly LinkableNote[], targetId: string): Backlink[] {
  const out: Backlink[] = []
  for (const note of notes) {
    if (note.id === targetId) continue
    for (const link of extractLinkTargets(splitNote(note.body).content)) {
      if (resolveNoteByTitle(notes, link.target)?.id === targetId) {
        out.push({ from_note_id: note.id, to_note_id: targetId, link_text: link.text })
        break // one entry per linking note
      }
    }
  }
  return out
}

/** Notes that name `title` in plain text without linking it. */
export function computeUnlinkedMentions(notes: readonly LinkableNote[], title: string, excludeId: string): UnlinkedMention[] {
  if (!title.trim()) return []
  const out: UnlinkedMention[] = []
  for (const note of notes) {
    if (note.id === excludeId) continue
    const count = countUnlinkedMentions(title, splitNote(note.body).content)
    if (count > 0) out.push({ fileId: note.id, title: note.title, count })
  }
  return out
}